import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Flex, Grid, Hide, Show } from '@chakra-ui/react';
import { Global } from '@emotion/react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { HiddenScrollbarContainer } from '@/components/atoms/HiddenScrollbarContainer';
import { ReadProgressBar } from '@/components/atoms/ReadProgressBar';
import { StickyCta } from '@/components/atoms/StickyCta';
import { DefaultLayout, Footer } from '@/components/layouts';
import { ArticleContent, ArticleFooter, ArticleHeader, RecommendedArticlesSection } from '@/components/sections/blog';
import { AboutArticle } from '@/components/sections/blog/AboutArticle/AboutArticle';
import { ArticleCategoryFilter } from '@/components/sections/blog/ArticleCategoryFilter/ArticleCategoryFilter';
import { LetsBuildSomethingSection, Newsletter } from '@/components/sections/common';
import { DynamicBlockSelector } from '@/components/sections/dynamic-blocks/DynamicBlockSelector';
import { SectionContentWrapper, SectionWrapper } from '@/components/wrappers';
import { PropsBuilder, useGetArticlesData } from '@/graphql/api';
import {
  useGetArticleBySlugQuery,
  useGetArticleCategoriesQuery,
  useGetArticlesQuery,
  useGetGlobalQuery,
  useGetProposedArticlesQuery,
} from '@/graphql/generated';
import { NextPageWithLayout } from '@/pages/_app';
import { DEFAULT_LOCALE, LOCALES, PAGE_REVALIDATE_TIME } from '@/utils/constants';
import { getImageAlt, getImageSrcWithBlurhash } from '@/utils/images';
import { GeneralPath } from '@/utils/models';
import { generateOpenGraphArticleObject } from '@/utils/seo-open-graph';

interface ArticleProps {
  preview: boolean;
  slug: string;
}

const MD_COL_WIDTH = '626px';

const Article: NextPageWithLayout<ArticleProps> = ({ slug, preview }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    content,
    title,
    titleMarkdown,
    authors,
    creationDate,
    tags,
    minutesOfRead,
    coverImage,
    category,
    dynamicBlocks,
    stickyCta,
  } = useGetArticlesData(slug, preview, 'all');
  const [containerAvailable, setContainerAvailable] = useState(false);
  const router = useRouter();
  const authorsData = authors?.data || [];
  const _tags = useMemo(
    () =>
      tags?.data
        .map(({ attributes }) => {
          return attributes?.name;
        })
        .filter((tag): tag is string => !!tag) || [],
    [tags],
  );
  const { height, width } = coverImage?.data?.attributes ?? {};

  useEffect(() => {
    if (containerRef.current) {
      setContainerAvailable(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current]);

  const onSelectCategory = useCallback(
    (id?: string) => {
      router.replace({
        pathname: `${GeneralPath.BLOG}`,
        query: id ? { selectedCategoryId: id } : {},
      });
    },
    [router],
  );

  return (
    <>
      <Global styles={{ html: { scrollBehavior: 'smooth' } }} />
      <Hide below="lg">
        <HiddenScrollbarContainer
          flexWrap={{
            base: 'nowrap',
            md: 'wrap',
          }}
          alignItems="center"
          justifyContent="center"
          overflowY="auto"
          gap={2}
          mt={2}
        >
          <ArticleCategoryFilter
            selectedCategoryId={category.data?.id || undefined}
            onSelectCategory={onSelectCategory}
          />
        </HiddenScrollbarContainer>
      </Hide>
      <ArticleHeader
        authors={authorsData}
        content={content}
        tags={tags}
        title={titleMarkdown || title}
        createdAt={creationDate}
        minutesOfRead={minutesOfRead || 0}
        mt={6}
      />

      <div ref={containerRef}>
        <SectionWrapper>
          <SectionContentWrapper paddingY={undefined}>
            <Grid
              templateColumns={{ lg: `minmax(19vw, 1fr) ${MD_COL_WIDTH} 1fr`, '3xl': `1fr ${MD_COL_WIDTH} 1fr` }}
              alignItems="start"
              justifyContent={{
                base: 'unset',
                md: 'center',
              }}
              gap={3}
            >
              <Show above="lg">
                <AboutArticle
                  authors={authorsData}
                  minutesOfRead={minutesOfRead || 0}
                  createdAt={creationDate}
                  content={content}
                  tags={tags}
                />
                {stickyCta?.title && (
                  <StickyCta
                    title={stickyCta?.title || ''}
                    subttitle={stickyCta?.subttitle || ''}
                    buttonCaption={stickyCta?.buttonCaption || ''}
                    buttonLink={stickyCta?.buttonLink || ''}
                    sectionId="articleFooter"
                  ></StickyCta>
                )}
              </Show>

              <ArticleContent content={content} />
            </Grid>
          </SectionContentWrapper>
        </SectionWrapper>
        {Array.isArray(dynamicBlocks)
          ? dynamicBlocks.map((block, index) => (
              <DynamicBlockSelector key={`${block?.__typename}-${index}`} typename={block?.__typename} {...block} />
            ))
          : null}

        {containerAvailable && <ReadProgressBar containerRef={containerRef.current} />}
        {coverImage ? (
          <Flex width="full" position="relative" overflow="hidden">
            <Image
              height={height ?? undefined}
              width={width ?? undefined}
              {...getImageSrcWithBlurhash(coverImage?.data?.attributes)}
              alt={getImageAlt(coverImage?.data?.attributes, title)}
              style={{ objectFit: 'cover', width: '100%' }}
            />
          </Flex>
        ) : null}
        <ArticleFooter createdAt={creationDate} authors={authorsData} />
        <Box mt="10">
          <Newsletter />
        </Box>
        <RecommendedArticlesSection tags={_tags} slug={slug} />
      </div>

      <Box w="100%" overflow="hidden">
        <LetsBuildSomethingSection />
      </Box>
    </>
  );
};

Article.getLayout = (page: ReactElement) => {
  const { metadata, preview, article } = page.props;

  return (
    <DefaultLayout metadata={metadata} preview={preview} article={generateOpenGraphArticleObject(article)}>
      {page}
      <Footer />
    </DefaultLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.ENV === 'staging') {
    return { paths: [], fallback: 'blocking' };
  }

  const articles = (await useGetArticlesQuery.fetcher()()).articles?.data!;
  const paths = articles.map((article) => ({
    params: { slug: article.attributes?.slug },
  }));

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<ArticleProps> = async (context) => {
  const { params, locale, preview = false } = context;
  const slug = params?.slug as string;

  const propsBuilder = new PropsBuilder();

  /* get global */
  propsBuilder.withQuery(useGetGlobalQuery, {
    variables: {
      locale,
    },
  });
  /* get article */
  const result = await propsBuilder.withQuery(useGetArticleBySlugQuery, {
    isPrivate: preview,
    variables: {
      slug,
      isPreview: preview,
      locale: 'all', // by default, Strapi creates unique "slag" for each localization version
    },
    getMetadata: (data) => data.article?.data?.attributes?.metadata,
  });
  const article = result.article?.data;

  if (!article) {
    return {
      notFound: true,
      revalidate: PAGE_REVALIDATE_TIME,
    };
  }
  const tags =
    article.attributes?.tags?.data.map((tag) => tag.attributes?.name).filter((tag): tag is string => !!tag) || [];

  /* get proposed articles */
  propsBuilder.withQuery(useGetProposedArticlesQuery, {
    variables: { tags, slug, locale },
  });
  /* get article categories */
  propsBuilder.withQuery(useGetArticleCategoriesQuery, {
    variables: { locale },
  });

  const lang = await serverSideTranslations(locale || DEFAULT_LOCALE, [LOCALES.COMMON]);

  return {
    props: {
      ...(await propsBuilder.build(lang)),
      preview,
      slug,
      article: article.attributes,
    },
    revalidate: PAGE_REVALIDATE_TIME,
  };
};

export default Article;
